import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, type Plan } from '../lib/api'
import { useAuth } from '../auth'
import { useBilling } from '../lib/queries'
import { Empty, PageHead, Skel } from '../components/ui'
import { money, number, thaiDate, thaiDateTime } from '../lib/format'

// Contract with handlers_planchange.go (part 141):
//   GET  /v1/billing/plan-changes            -> PlanChangeRow[]
//   GET  /v1/billing/plan-changes/quote      -> PlanChangeQuote
//   POST /v1/billing/plan-changes            { to_plan } -> PlanChangeRow
// The current plan and the price list come from the existing /v1/billing snapshot,
// so this screen adds no second source of truth for pricing.

interface PlanChangeQuote {
  subscription_id: string
  current_plan: Plan
  target_plan: Plan
  current_period_start: string
  current_period_end: string
  effective_on: string
  days_in_period: number
  days_remaining: number
  charge_satang: number
  credit_satang: number
  net_satang: number
  direction: 'upgrade' | 'downgrade'
}

interface PlanChangeRow {
  id: string
  from_plan: string
  to_plan: string
  effective_on: string
  proration_satang: number
  credit_satang: number
  net_satang: number
  created_by?: string
  created_at: string
}

const statusLabel: Record<string, string> = {
  trialing: 'ทดลองใช้',
  active: 'ใช้งาน',
  past_due: 'ค้างชำระ',
  canceled: 'ยกเลิก',
}

const directionLabel: Record<string, string> = {
  upgrade: 'อัปเกรด',
  downgrade: 'ดาวน์เกรด',
}

/** Mirrors the ladder in internal/domain/proration; 0 keeps an unknown key last. */
function planRank(id: string): number {
  if (id === 'starter') return 1
  if (id === 'growth') return 2
  if (id === 'enterprise') return 3
  return 0
}

function planName(plans: Plan[], id: string): string {
  return plans.find((p) => p.id === id)?.name ?? id
}

function netLabel(net: number): string {
  if (net > 0) return 'ยอดที่ต้องชำระเพิ่ม'
  if (net < 0) return 'เครดิตยกไปรอบถัดไป'
  return 'ไม่มีส่วนต่าง'
}

export function PlanChanges() {
  const { me } = useAuth()
  const qc = useQueryClient()
  const billing = useBilling()
  const snap = billing.data
  const plans = snap?.plans ?? []
  const currentPlanId = snap?.plan.id ?? ''

  const [target, setTarget] = useState('')
  const [done, setDone] = useState<PlanChangeRow | null>(null)

  // Default to the next rung up; a tenant on enterprise falls back to the first
  // other plan so the selector is never empty.
  useEffect(() => {
    if (!snap || target) return
    const next =
      snap.plans.find((p) => planRank(p.id) > planRank(snap.plan.id)) ??
      snap.plans.find((p) => p.id !== snap.plan.id)
    if (next) setTarget(next.id)
  }, [snap, target])

  const quote = useQuery({
    queryKey: ['plan-change-quote', target],
    queryFn: () => api<PlanChangeQuote>(`/v1/billing/plan-changes/quote?to_plan=${encodeURIComponent(target)}`),
    enabled: target !== '' && target !== currentPlanId,
    staleTime: 15_000,
    retry: false,
  })

  const history = useQuery({
    queryKey: ['plan-changes'],
    queryFn: () => api<PlanChangeRow[]>('/v1/billing/plan-changes'),
    staleTime: 15_000,
  })

  const submit = useMutation({
    mutationFn: () => apiWrite<PlanChangeRow>('/v1/billing/plan-changes', 'POST', { to_plan: target }),
    onSuccess: (row) => {
      setDone(row)
      void qc.invalidateQueries({ queryKey: ['billing'] })
      void qc.invalidateQueries({ queryKey: ['plan-changes'] })
      void qc.invalidateQueries({ queryKey: ['plan-change-quote'] })
    },
  })

  if (billing.isLoading) {
    return (
      <>
        <PageHead title="เปลี่ยนแพ็กเกจ" sub="คิดส่วนต่างตามจำนวนวันที่เหลือในรอบบิล" />
        <div className="stack"><Skel h={210} /><Skel h={150} /></div>
      </>
    )
  }

  if (billing.isError || !snap) {
    return (
      <>
        <PageHead title="เปลี่ยนแพ็กเกจ" sub="คิดส่วนต่างตามจำนวนวันที่เหลือในรอบบิล" />
        <Empty title="โหลดข้อมูลแพ็กเกจไม่ได้" body="ลองรีเฟรชหน้าอีกครั้ง หรือตรวจสอบการเชื่อมต่อกับเซิร์ฟเวอร์" />
      </>
    )
  }

  const sub = snap.subscription
  const canManage = me?.role === 'owner' || me?.role === 'admin'
  const isCurrent = target === '' || target === currentPlanId
  const q = quote.data
  const rows = history.data ?? []
  const busy = submit.isPending

  return (
    <>
      <PageHead title="เปลี่ยนแพ็กเกจ" sub="คิดส่วนต่างตามวันที่เหลือของรอบบิล · ราคาเป็นสตางค์ · ยังไม่รวม VAT 7%" />

      <div className="grid-main">
        <div className="card card-pad">
          <div className="card-label">แพ็กเกจปัจจุบัน</div>
          <div className="row" style={{ marginTop: 12, alignItems: 'baseline', gap: 12 }}>
            <span className="display-m">{snap.plan.name}</span>
            <span className="chip gold">{statusLabel[sub.status] ?? sub.status}</span>
          </div>

          <div className="row" style={{ marginTop: 22, gap: 30, flexWrap: 'wrap' }}>
            <div>
              <div className="eyebrow">รอบบิล</div>
              <div style={{ marginTop: 6, fontSize: 12.5 }}>
                {thaiDate(sub.current_period_start)} — {thaiDate(sub.current_period_end)}
              </div>
            </div>
            <div>
              <div className="eyebrow">ค่าบริการ/เดือน</div>
              <div className="heading-l tnum" style={{ marginTop: 6 }}>{money(snap.plan.price_satang)}</div>
            </div>
            <div>
              <div className="eyebrow">เอกสารในแพ็กเกจ</div>
              <div className="heading-l tnum" style={{ marginTop: 6 }}>{number(snap.plan.included_docs)}</div>
            </div>
            <div>
              <div className="eyebrow">ที่นั่ง</div>
              <div className="heading-l tnum" style={{ marginTop: 6 }}>{sub.seats}</div>
            </div>
          </div>

          <div className="rule" style={{ margin: '22px 0 16px' }} />

          <div className="card-label">เลือกแพ็กเกจใหม่</div>
          <div style={{ marginTop: 10 }}>
            {plans.map((p) => {
              const active = p.id === target
              const current = p.id === currentPlanId
              return (
                <button
                  key={p.id}
                  type="button"
                  aria-pressed={active}
                  disabled={current || busy}
                  onClick={() => { setDone(null); setTarget(p.id) }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', marginTop: 8,
                    padding: '13px 15px',
                    borderRadius: 'var(--radius-md)',
                    border: `1px solid ${active ? 'var(--color-gold)' : 'var(--color-hairline)'}`,
                    background: active ? 'rgba(200, 169, 106, 0.07)' : 'var(--color-wash)',
                    color: 'inherit',
                    cursor: current ? 'default' : busy ? 'wait' : 'pointer',
                    opacity: current ? 0.55 : 1,
                    transition: 'border-color var(--micro) var(--ease-standard)',
                  }}
                >
                  <div className="row">
                    <span style={{ fontSize: 13 }}>{p.name}</span>
                    {current ? <span className="chip">ปัจจุบัน</span> : null}
                    {active ? <span className="chip gold">เลือกอยู่</span> : null}
                    <span className="tnum" style={{ marginLeft: 'auto' }}>
                      {money(p.price_satang)}
                      <span className="dim" style={{ fontSize: 11 }}> /เดือน</span>
                    </span>
                  </div>
                  <div className="dim" style={{ fontSize: 11.5, marginTop: 5 }}>
                    {number(p.included_docs)} เอกสาร · {p.included_seats} ที่นั่ง · เกินแพ็กเกจ {money(p.overage_satang)}/เอกสาร
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        <div className="card card-pad">
          <div className="card-label">ตัวอย่างส่วนต่าง</div>

          {isCurrent ? (
            <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.7, marginTop: 14 }}>
              เลือกแพ็กเกจที่ต้องการเปลี่ยนก่อน ระบบจะคำนวณส่วนต่างจากจำนวนวันที่เหลือในรอบบิลให้ทันที
            </p>
          ) : quote.isLoading ? (
            <div className="stack" style={{ marginTop: 14 }}><Skel h={54} /><Skel h={120} /></div>
          ) : quote.isError || !q ? (
            <p style={{ fontSize: 12.5, lineHeight: 1.7, marginTop: 14, color: 'var(--color-amber)' }}>
              คำนวณส่วนต่างไม่ได้ — แพ็กเกจปลายทางอาจไม่ถูกต้อง หรือสมาชิกไม่ได้อยู่ในสถานะที่เปลี่ยนได้
            </p>
          ) : (
            <>
              <div className="row" style={{ marginTop: 12, alignItems: 'baseline', gap: 10 }}>
                <span className="display-m">{q.target_plan.name}</span>
                <span className="chip gold">{directionLabel[q.direction] ?? q.direction}</span>
              </div>
              <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>
                มีผล {thaiDate(q.effective_on)} · เหลือ {q.days_remaining} จาก {q.days_in_period} วัน
              </div>

              <dl className="kv" style={{ marginTop: 16, gridTemplateColumns: '1fr auto' }}>
                <dt>ค่าบริการใหม่ (ตามวันที่เหลือ)</dt>
                <dd className="tnum" style={{ textAlign: 'right' }}>{money(q.charge_satang)}</dd>
                <dt>เครดิตแพ็กเกจเดิม</dt>
                <dd className="tnum" style={{ textAlign: 'right' }}>−{money(q.credit_satang)}</dd>
              </dl>

              <div className="rule" style={{ margin: '14px 0' }} />

              <div className="row">
                <span className="dim" style={{ fontSize: 12 }}>{netLabel(q.net_satang)}</span>
                <span className="display-m tnum" style={{ marginLeft: 'auto' }}>{money(q.net_satang)}</span>
              </div>

              <button
                className="btn btn-primary"
                style={{ width: '100%', marginTop: 18 }}
                disabled={!canManage || busy}
                onClick={() => submit.mutate()}
              >
                {busy ? 'กำลังบันทึก…' : `ยืนยันเปลี่ยนเป็น ${q.target_plan.name}`}
              </button>

              {!canManage ? (
                <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
                  เฉพาะเจ้าของหรือผู้ดูแลระบบเท่านั้นที่เปลี่ยนแพ็กเกจได้
                </div>
              ) : (
                <div className="dim" style={{ fontSize: 11, marginTop: 10, lineHeight: 1.6 }}>
                  ส่วนต่างจะถูกเพิ่มในใบแจ้งหนี้รอบถัดไป · เครดิตที่เหลือจะยกไปหักในรอบถัดไปเช่นกัน
                </div>
              )}

              {submit.isError ? (
                <div style={{ fontSize: 11.5, marginTop: 10, color: 'var(--color-amber)' }}>
                  {submit.error instanceof Error ? submit.error.message : 'เปลี่ยนแพ็กเกจไม่สำเร็จ'}
                </div>
              ) : null}
            </>
          )}

          {done ? (
            <div
              style={{
                marginTop: 16, padding: '12px 14px', borderRadius: 'var(--radius-md)',
                border: '1px solid rgba(200, 169, 106, 0.35)', background: 'rgba(200, 169, 106, 0.06)',
              }}
            >
              <div style={{ fontSize: 12.5 }}>
                เปลี่ยนเป็น {planName(plans, done.to_plan)} เรียบร้อย
              </div>
              <div className="dim tnum" style={{ fontSize: 11, marginTop: 4 }}>
                ค่าบริการใหม่ {money(done.proration_satang)} · เครดิต {money(done.credit_satang)} · {thaiDateTime(done.created_at)}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div style={{ marginTop: 24 }}>
        <div className="card-label" style={{ marginBottom: 10 }}>ประวัติการเปลี่ยนแพ็กเกจ</div>
        {history.isLoading ? (
          <div className="card card-pad"><Skel h={64} /></div>
        ) : rows.length === 0 ? (
          <div className="card card-pad dim" style={{ fontSize: 12.5 }}>
            ยังไม่มีการเปลี่ยนแพ็กเกจ — เมื่อเปลี่ยนแล้ว ทุกรายการจะถูกบันทึกที่นี่พร้อมยอดที่คิดไว้
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>วันที่มีผล</th>
                  <th>จาก</th>
                  <th>ไป</th>
                  <th className="num">ค่าบริการใหม่</th>
                  <th className="num">เครดิตเดิม</th>
                  <th className="num">สุทธิ</th>
                  <th>บันทึกเมื่อ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} style={{ cursor: 'default' }}>
                    <td>{thaiDate(row.effective_on)}</td>
                    <td className="dim">{planName(plans, row.from_plan)}</td>
                    <td>{planName(plans, row.to_plan)}</td>
                    <td className="num">{money(row.proration_satang)}</td>
                    <td className="num dim">{money(row.credit_satang)}</td>
                    <td className="num">{money(row.net_satang)}</td>
                    <td className="dim">{thaiDateTime(row.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}
